<template>
    <div id="tradeTrackerSummaryView">
        <div class="table-header border-t">
            <div class="flex flex-wrap w-full xs:text-center">
                <div class="relative w-full sm:w-auto pr-3 xs:pr-0 xs:pb-2 font-bold">
                    {{ summaryTitle }} Summary
                </div>
                <div class="ml-auto w-full sm:w-auto print:hidden" v-if="!allSummary">
                    <span class="text-blue-500 text-xs hover:text-blue-400 cursor-pointer" @click="handleViewEmit">
                        View All Porfolios (Summary)
                    </span>
                </div>
            </div>
        </div>

        <div class="flex flex-wrap w-full border text-sm">
            <div class="inline-flex xs:w-full w-1/2 px-4 py-1 border-b print:w-1/2">
                <div class="inline-block md:w-1/2 sm:w-1/2 w-auto xs:mr-4 sm:mr-4 mr-0">
                    <span v-tooltip="{ content: 'Profit / Loss of active trades since the previous day\'s close. Also includes Profit / Loss of trades that were liquidated today.', theme: 'info' }">
                        Today's Return:
                    </span>
                </div>
                <div class="inline-block">
                    <span :class="getClass(portfolioTotal.raw.daily_net_return)">{{ portfolioTotal.daily_net_return || 'N/A' }}</span>
                </div>
            </div>
            <div class="inline-flex xs:w-full w-1/2 px-4 py-1 border-b print:w-1/2">
                <div class="inline-block md:w-1/2 sm:w-1/2 w-auto xs:mr-4 sm:mr-4 mr-0">
                    <span v-tooltip="{ content: 'The amount of cash tied up in active (not liquidated) trades. Spread margins are not included.', theme: 'info' }">
                        Active Margin / Premium:
                    </span>
                </div>
                <div class="inline-block">
                    {{ portfolioTotal.margin || 'N/A' }}
                </div>
            </div>

            <div class="inline-flex xs:w-full w-1/2 px-4 py-1 border-b print:w-1/2">
                <div class="inline-block md:w-1/2 sm:w-1/2 w-auto xs:mr-4 sm:mr-4 mr-0">
                    <span v-tooltip="{ content: 'Profit or Loss of active (not liquidated) trades.', theme: 'info' }">
                        Active Profit / Loss:
                    </span>
                </div>
                <div class="inline-block">
                    <span :class="getClass(portfolioTotal.raw.active_return)">{{ portfolioTotal.active_return || 'N/A' }}</span>
                </div>
            </div>
            <div class="inline-flex xs:w-full w-1/2 px-4 py-1 border-b print:w-1/2">
                <div class="inline-block md:w-1/2 sm:w-1/2 w-auto xs:mr-4 sm:mr-4 mr-0">
                    <span v-tooltip="{ content: 'The sum of the starting cash plus closed profit/loss.<br/>Account Value = Starting Value Cash + Closed Profit/Loss + Active Profit / Loss.', theme: 'info' }">
                        Account Value:
                    </span>
                </div>
                <div class="inline-block">
                    {{ portfolioTotal.account_value || 'N/A' }}
                </div>
            </div>

            <div class="inline-flex xs:w-full w-1/2 px-4 py-1 border-b print:w-1/2">
                <div class="inline-block md:w-1/2 sm:w-1/2 w-auto xs:mr-4 sm:mr-4 mr-0">
                    <span v-tooltip="{ content: 'Profit or loss from liquidated (closed) trades.', theme: 'info' }">
                        Closed Profit / Loss:
                    </span>
                </div>
                <div class="inline-block">
                    <span :class="getClass(portfolioTotal.raw.closed_return)">{{ portfolioTotal.closed_return || 'N/A' }}</span>
                </div>
            </div>
            <div class="inline-flex xs:w-full w-1/2 px-4 py-1 border-b print:w-1/2">
                <template v-if="portfolio.adjust_cash || allSummary">
                    <div class="inline-block md:w-1/2 sm:w-1/2 w-auto xs:mr-4 sm:mr-4 mr-0">
                        <span v-tooltip="{ content: 'The amount of cash available to spend if Cash Adjust is turned on in portfolio settings.<br/>Available Cash = Starting Value Cash - Margin + Closed Profit/Loss', theme: 'info' }">
                            Available Cash<template v-if="!allSummary"> ({{ portfolio.adjust_cash ? 'adjustable' : 'non-adjustable' }})</template>:
                        </span>
                    </div>
                    <div class="inline-block">
                        {{ portfolioTotal.available_cash || 'N/A' }}
                    </div>
                </template>
            </div>

            <div class="inline-flex xs:w-full w-1/2 px-4 py-1">
                <div class="inline-block md:w-1/2 sm:w-1/2 w-auto xs:mr-4 sm:mr-4 mr-0">
                    <span v-tooltip="{ content: 'Profit or Loss from all trades.', theme: 'info' }">
                        Net Profit/Loss:
                    </span>
                </div>
                <div class="inline-block">
                    <span :class="getClass(portfolioTotal.raw.net_return)">{{ portfolioTotal.net_return || 'N/A' }}</span>
                </div>
            </div>
        </div>

        <div class="mt-4 bg-tan-200 border-t-2 border-blue-600" v-if="allSummary">
            <div class="border px-3 pt-2">
                <notes
                    id="summaryNotes"
                    label="Notes:"
                    placeholder="Enter notes..."
                    v-model="modelNotes"
                    :initialNote="modelNotes"
                    @apply="saveSummaryNotes"
                />
                <div class="text-gray-500 text-xs -mt-1 mb-2"><em>Changes to notes are saved automatically.</em></div>
            </div>
        </div>

        <flash-message :secondsToDisplay="3" ref="flash"></flash-message>

    </div>
</template>

<script setup>
import { ref, onMounted } from 'vue';
import Notes from '../../Notes';

const props = defineProps({
    portfolio: {
        type: Object,
        required: true
    },
    allSummary: Boolean,
});

const flash = ref(null);
const emit = defineEmits(['onGetAllPortfoliosSummary']);

// const portfolioData = ref(props.allSummary ? props.portfolio.portfolios :  props.portfolio);
const portfolioTotal = ref(props.allSummary ? props.portfolio.grand_totals : props.portfolio.totals);
const summaryTitle = ref(props.allSummary ? 'Grand Totals' : props.portfolio.caption);

const modelNotes = ref(props.portfolio.user_notes || '');

const handleViewEmit = () => {
    emit('onGetAllPortfoliosSummary');
};

// const toCurrency = computed(() => {
//     return (value) => {
//         if (typeof value !== "number") {
//             return value || '-';
//         }
//         const formatter = new Intl.NumberFormat('en-US', {
//             style: 'currency',
//             currency: 'USD',
//             minimumFractionDigits: 2
//         });
//         return formatter.format(value);
//     };
// });

const getClass = (value) => {
    if (typeof (value) == 'undefined') {
        return '-';
    }
    if (typeof (value) === 'string') {
        value = value.replace(/\$|,/g, '');
    }
    return value > 0 ? 'text-up' : value < 0 ? 'text-down' : 'text-unch';
};

const saveSummaryNotes = (user_notes) => {
    const route = window.route('api.trade-tracker.update-user');
    axios.post(route, { user_notes: user_notes })
        .then((response) => {
            flash.value.display('Saved summary notes');
        })
        .catch((error) => {
            console.error(error);
            flash.value.error('Error saving your notes...');
        });
};

onMounted(() => {
    if (props.allSummary) {
        // portfolioData.value = props.portfolio.portfolios;
        portfolioTotal.value = props.portfolio.grand_totals;
        summaryTitle.value = 'Grand Totals';
    } else {
        // portfolioData.value = props.portfolio;
        portfolioTotal.value = props.portfolio.totals;
        summaryTitle.value = props.portfolio.caption;
    }
});
</script>


<style lang="scss">
#tradeTrackerSummaryView {
    span.has-tooltip {
        @apply cursor-pointer border-b border-dashed border-gray-500;
    }

    table {
        thead {
            td, th {
                @apply px-3 py-2;
            }
        }
        tbody {
            td, th {
                @apply px-3;
            }
        }
        .sortable {
            @apply cursor-pointer;
        }
    }
}
</style>
